import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  FormControlLabel,
  TextField,
  Collapse,
  IconButton,
  useTheme,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Help as HelpIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  KeyboardArrowRight as ChevronRightIcon,
  KeyboardArrowDown as ChevronDownIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableBody,
  StyledTablePagination,
  StyledTableSortLabel,
  Table,
  TableCell,
  TableRow,
} from '@/components/StyledTable';
import { DateRangePicker } from '@/components/DateRangePicker';
import { HelpModal } from '@/components/Modals';
import { TrendChart } from '@/components/TrendChart';
import advertisersService from '@/services/advertisers.service';
import dayjs from 'dayjs';
import { RawReportData, ProcessedReportData, DailyBreakdown, ReportType } from '@/types/insights.types';
import { toNum, safeDiv, formatCurrency, formatNumber, copyToClipboard } from '@/utils/insights.utils';
import { exportToCSV } from '@/utils/csv.utils';

const InsightsPage = () => {
  const theme = useTheme();

  // State
  const [reportType, setReportType] = useState<ReportType>('campaign');
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState<RawReportData[]>([]);
  const [error, setError] = useState('');
  const [groupByDate, setGroupByDate] = useState(false);
  const [bulkFiltersExpanded, setBulkFiltersExpanded] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [expandedCampaigns, setExpandedCampaigns] = useState<{ [key: string]: boolean }>({});
  const [showFilters, setShowFilters] = useState(true);

  // Bulk filters
  const [bulkFilters, setBulkFilters] = useState({
    campaignIds: '',
    adsetIds: '',
    adIds: '',
  });

  // Column filters
  const [filters, setFilters] = useState<{ [key: string]: string }>({});

  // Advertiser filter
  const [selectedAdvertisers, setSelectedAdvertisers] = useState<string[]>([]);

  // Help modal
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Date range
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const start = new Date(yesterday);
  start.setDate(start.getDate() - 7);

  const [dateRange, setDateRange] = useState({
    from: dayjs(start).format('YYYY-MM-DD'),
    to: dayjs(yesterday).format('YYYY-MM-DD'),
  });

  // Pagination & Sorting
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [orderBy, setOrderBy] = useState<string>('spend');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Generate Report
  const generateReport = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await advertisersService.getReportsData({
        fromDate: dateRange.from,
        toDate: dateRange.to,
      });

      setRawData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate on mount
  useEffect(() => {
    generateReport();
  }, []);

  // Process data - matching old reports.html logic
  const processedData = useMemo((): ProcessedReportData[] => {
    if (rawData.length === 0) return [];

    const grouped: { [key: string]: any } = {};

    rawData.forEach((row) => {
      const idKey = reportType === 'campaign' ? row.campaign_id : row.ad_id;

      if (!idKey) {
        return;
      }

      const date = row.date || null;
      const groupKey = groupByDate && date ? `${date}|${idKey}` : idKey;

      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          date: date,
          campaignName: row.campaignName || '',
          campaignId: row.campaign_id || '',
          advertiser: row.advertiserName || 'Unknown',
          adId: reportType === 'ad' ? (row.ad_id || '') : undefined,
          adsetId: reportType === 'ad' ? (row.adset_id || '') : undefined,
          spend: 0,
          revenue: 0,
          clicks: 0,
          fbClicks: 0,
          purchases: 0,
          _dates: [] as any[],
        };
      }

      const group = grouped[groupKey];
      group.spend += toNum(row.fbSpend);
      group.revenue += toNum(row.reportedNetRevenue);
      group.clicks += toNum(row.reportedClicks);
      group.fbClicks += toNum(row.linkClicks);
      group.purchases += toNum(row.fbPurchase);

      // Track dates for trend calculation
      if (!groupByDate && date) {
        const existingDateEntry = group._dates.find((d: any) => d.date === date);
        if (existingDateEntry) {
          existingDateEntry.spend += toNum(row.fbSpend);
          existingDateEntry.revenue += toNum(row.reportedNetRevenue);
          existingDateEntry.clicks += toNum(row.reportedClicks);
          existingDateEntry.purchases += toNum(row.fbPurchase);
        } else {
          group._dates.push({
            date: date,
            spend: toNum(row.fbSpend),
            revenue: toNum(row.reportedNetRevenue),
            clicks: toNum(row.reportedClicks),
            purchases: toNum(row.fbPurchase),
          });
        }
      }
    });

    // Convert to array and calculate metrics
    const result = Object.values(grouped).map((group: any): ProcessedReportData => {
      const currentRoas = safeDiv(group.revenue, group.spend);
      const currentRpc = safeDiv(group.revenue, group.clicks);

      // Calculate trends
      let roasTrend: number | null = null;
      let rpcTrend: number | null = null;

      if (!groupByDate && group._dates && group._dates.length > 1) {
        group._dates.sort((a: any, b: any) => a.date.localeCompare(b.date));

        const datesToAnalyze = group._dates.length > 7 ? group._dates.slice(-7) : group._dates;
        const midpoint = Math.floor(datesToAnalyze.length / 2);
        const firstHalf = datesToAnalyze.slice(0, midpoint);
        const secondHalf = datesToAnalyze.slice(midpoint);

        const firstSpend = firstHalf.reduce((sum: number, d: any) => sum + d.spend, 0);
        const firstRevenue = firstHalf.reduce((sum: number, d: any) => sum + d.revenue, 0);
        const firstClicks = firstHalf.reduce((sum: number, d: any) => sum + d.clicks, 0);

        const secondSpend = secondHalf.reduce((sum: number, d: any) => sum + d.spend, 0);
        const secondRevenue = secondHalf.reduce((sum: number, d: any) => sum + d.revenue, 0);
        const secondClicks = secondHalf.reduce((sum: number, d: any) => sum + d.clicks, 0);

        const firstRoas = safeDiv(firstRevenue, firstSpend);
        const secondRoas = safeDiv(secondRevenue, secondSpend);
        const firstRpc = safeDiv(firstRevenue, firstClicks);
        const secondRpc = safeDiv(secondRevenue, secondClicks);

        if (firstRoas && secondRoas) {
          roasTrend = ((secondRoas - firstRoas) / firstRoas) * 100;
        }
        if (firstRpc && secondRpc) {
          rpcTrend = ((secondRpc - firstRpc) / firstRpc) * 100;
        }
      }

      // Create daily breakdown
      const dailyBreakdown: DailyBreakdown[] | null = !groupByDate && group._dates
        ? group._dates.map((d: any) => ({
            date: d.date,
            spend: d.spend,
            revenue: d.revenue,
            clicks: d.clicks,
            purchases: d.purchases,
            pnl: d.revenue - d.spend,
            roas: safeDiv(d.revenue, d.spend),
            avgRpc: safeDiv(d.revenue, d.clicks),
            avgCpp: d.purchases > 0 ? safeDiv(d.spend, d.purchases) : null,
          }))
        : null;

      return {
        date: group.date,
        campaignName: group.campaignName,
        campaignId: group.campaignId,
        advertiser: group.advertiser,
        adId: group.adId,
        adsetId: group.adsetId,
        spend: group.spend,
        revenue: group.revenue,
        clicks: group.clicks,
        fbClicks: group.fbClicks,
        purchases: group.purchases,
        pnl: group.revenue - group.spend,
        roas: currentRoas,
        avgRpc: currentRpc,
        avgCpp: group.purchases > 0 ? safeDiv(group.spend, group.purchases) : null,
        cplc: safeDiv(group.spend, group.fbClicks),
        roasTrend: roasTrend,
        rpcTrend: rpcTrend,
        dailyBreakdown: dailyBreakdown,
      };
    });

    // Sort by spend descending by default
    result.sort((a, b) => b.spend - a.spend);

    return result;
  }, [rawData, reportType, groupByDate]);

  // Apply filters
  const filteredData = useMemo(() => {
    let filtered = [...processedData];

    // Apply bulk filters
    if (bulkFilters.campaignIds) {
      const ids = bulkFilters.campaignIds.split(',').map(id => id.trim()).filter(Boolean);
      if (ids.length > 0) {
        filtered = filtered.filter(row => ids.includes(row.campaignId));
      }
    }

    if (bulkFilters.adsetIds && reportType === 'ad') {
      const ids = bulkFilters.adsetIds.split(',').map(id => id.trim()).filter(Boolean);
      if (ids.length > 0) {
        filtered = filtered.filter(row => row.adsetId && ids.includes(row.adsetId));
      }
    }

    if (bulkFilters.adIds && reportType === 'ad') {
      const ids = bulkFilters.adIds.split(',').map(id => id.trim()).filter(Boolean);
      if (ids.length > 0) {
        filtered = filtered.filter(row => row.adId && ids.includes(row.adId));
      }
    }

    // Apply column filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        filtered = filtered.filter(row => {
          const cellValue = String((row as any)[key] || '').toLowerCase();
          return cellValue.includes(value.toLowerCase());
        });
      }
    });

    // Apply advertiser filter
    if (selectedAdvertisers.length > 0) {
      filtered = filtered.filter(row => selectedAdvertisers.includes(row.advertiser));
    }

    return filtered;
  }, [processedData, bulkFilters, filters, selectedAdvertisers, reportType]);

  // Extract unique advertisers
  const uniqueAdvertisers = useMemo(() => {
    const advertisers = [...new Set(processedData.map(row => row.advertiser))].filter(a => a && a !== 'Unknown').sort();
    return advertisers;
  }, [processedData]);

  // Sorted data
  const sortedData = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) => {
      const aValue = (a as any)[orderBy];
      const bValue = (b as any)[orderBy];

      if (typeof aValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return order === 'asc' ? (aValue || 0) - (bValue || 0) : (bValue || 0) - (aValue || 0);
    });

    return sorted;
  }, [filteredData, orderBy, order]);

  // Paginated data
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handlers
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleBulkFilter = (field: string, value: string) => {
    setBulkFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleFilter = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearAllFilters = () => {
    setBulkFilters({
      campaignIds: '',
      adsetIds: '',
      adIds: '',
    });
    setFilters({});
    setSelectedAdvertisers([]);
  };

  const toggleCampaign = (campaignId: string) => {
    setExpandedCampaigns(prev => ({
      ...prev,
      [campaignId]: !prev[campaignId],
    }));
  };


  const handleExportCSV = () => {
    exportToCSV({
      data: sortedData,
      columns: [
        { header: 'Date', getValue: (row) => row.date || '', condition: groupByDate },
        { header: 'Campaign Name', getValue: (row) => row.campaignName },
        { header: 'Campaign ID', getValue: (row) => row.campaignId },
        { header: 'Advertiser', getValue: (row) => row.advertiser },
        { header: 'Ad ID', getValue: (row) => row.adId || '', condition: reportType === 'ad' },
        { header: 'Adset ID', getValue: (row) => row.adsetId || '', condition: reportType === 'ad' },
        { header: 'P&L', getValue: (row) => row.pnl.toFixed(2) },
        { header: 'Spend', getValue: (row) => row.spend.toFixed(2) },
        { header: 'Revenue', getValue: (row) => row.revenue.toFixed(2) },
        { header: 'ROAS', getValue: (row) => row.roas ? row.roas.toFixed(2) : '' },
        { header: 'AVG RPC', getValue: (row) => row.avgRpc ? row.avgRpc.toFixed(2) : '' },
        { header: 'Cost/Purchase', getValue: (row) => row.avgCpp ? row.avgCpp.toFixed(2) : '' },
        { header: 'Cost/Click', getValue: (row) => row.cplc ? row.cplc.toFixed(2) : '' },
        { header: 'ROAS Trend %', getValue: (row) => row.roasTrend !== null ? row.roasTrend.toFixed(1) : '', condition: !groupByDate },
        { header: 'RPC Trend %', getValue: (row) => row.rpcTrend !== null ? row.rpcTrend.toFixed(1) : '', condition: !groupByDate },
      ],
      filename: `${reportType}-report-${dayjs().format('YYYY-MM-DD')}`,
    });
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Insights
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generate detailed Ad or Campaign level reports with performance metrics.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<HelpIcon />}
          onClick={() => setShowHelpModal(true)}
          sx={{ minWidth: 100 }}
        >
          Help
        </Button>
      </Box>

      {/* Controls Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
            {/* Report Type */}
            <FormControl sx={{ minWidth: 200 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Report Type
              </Typography>
              <Select
                value={reportType}
                onChange={(e) => setReportType(e.target.value as ReportType)}
                size="small"
                sx={{
                  height: 40,
                  '& .MuiSelect-select': {
                    py: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }
                }}
              >
                <MenuItem value="campaign">Campaign Report</MenuItem>
                <MenuItem value="ad">Ad Report</MenuItem>
              </Select>
            </FormControl>

            {/* Date Range */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Date Range
              </Typography>
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                placeholder="Select date range"
              />
            </Box>

            {/* Group by Date */}
            <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupByDate}
                    onChange={(e) => setGroupByDate(e.target.checked)}
                  />
                }
                label="Group by Date"
                sx={{ mb: 0 }}
              />
            </Box>

            {/* Generate Report Button */}
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayIcon />}
              onClick={generateReport}
              disabled={loading}
              sx={{ height: 40 }}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>

            {/* Export CSV Button */}
            {sortedData.length > 0 && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={{ height: 40 }}
              >
                Export CSV
              </Button>
            )}

            {/* Last Updated */}
            {lastUpdated && (
              <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Last updated: {lastUpdated.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {sortedData.length} of {processedData.length} records
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Bulk Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              startIcon={bulkFiltersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setBulkFiltersExpanded(!bulkFiltersExpanded)}
              sx={{ textTransform: 'none', fontSize: '1.1rem', fontWeight: 600 }}
            >
              <FilterIcon sx={{ mr: 1 }} />
              Bulk Filters
            </Button>
            {(bulkFilters.campaignIds || bulkFilters.adsetIds || bulkFilters.adIds || selectedAdvertisers.length > 0) && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearAllFilters}
              >
                Clear All
              </Button>
            )}
          </Box>

          <Collapse in={bulkFiltersExpanded} sx={{ mt: bulkFiltersExpanded ? 2 : 0 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Campaign IDs
                </Typography>
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  placeholder="Paste comma-separated Campaign IDs..."
                  value={bulkFilters.campaignIds}
                  onChange={(e) => handleBulkFilter('campaignIds', e.target.value)}
                />
                <Typography variant="caption" color="text.secondary">
                  Example: 123456, 789012, 345678
                </Typography>
              </Box>

              {reportType === 'ad' && (
                <>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Adset IDs
                    </Typography>
                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      size="small"
                      placeholder="Paste comma-separated Adset IDs..."
                      value={bulkFilters.adsetIds}
                      onChange={(e) => handleBulkFilter('adsetIds', e.target.value)}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Example: 123456, 789012, 345678
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Ad IDs
                    </Typography>
                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      size="small"
                      placeholder="Paste comma-separated Ad IDs..."
                      value={bulkFilters.adIds}
                      onChange={(e) => handleBulkFilter('adIds', e.target.value)}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Example: 123456, 789012, 345678
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Report Table */}
      {rawData.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {reportType === 'campaign' ? 'Campaign' : 'Ad'} Report
              </Typography>
              <Button
                size="small"
                startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>
            </Box>

            <StyledTableContainer>
              <Table stickyHeader>
                <StyledTableHead>
                  <TableRow>
                    {groupByDate && (
                      <TableCell sx={{ minWidth: 120 }}>
                        <Box>
                          <StyledTableSortLabel
                            active={orderBy === 'date'}
                            direction={orderBy === 'date' ? order : 'asc'}
                            onClick={() => handleRequestSort('date')}
                          >
                            Date
                          </StyledTableSortLabel>
                        </Box>
                        {showFilters && (
                          <TextField
                            size="small"
                            placeholder="Filter..."
                            value={filters.date || ''}
                            onChange={(e) => handleFilter('date', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              mt: 1,
                              width: '100%',
                              '& .MuiInputBase-root': {
                                backgroundColor: 'background.paper',
                                fontSize: '0.75rem',
                                height: 32,
                              }
                            }}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell sx={{ minWidth: 500 }}>
                      <Box>
                        <StyledTableSortLabel
                          active={orderBy === 'campaignName'}
                          direction={orderBy === 'campaignName' ? order : 'asc'}
                          onClick={() => handleRequestSort('campaignName')}
                        >
                          Campaign Name
                        </StyledTableSortLabel>
                      </Box>
                      {showFilters && (
                        <TextField
                          size="small"
                          placeholder="Filter..."
                          value={filters.campaignName || ''}
                          onChange={(e) => handleFilter('campaignName', e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            mt: 1,
                            width: '100%',
                            '& .MuiInputBase-root': {
                              backgroundColor: 'background.paper',
                              fontSize: '0.75rem',
                              height: 32,
                            }
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <Box>
                        <StyledTableSortLabel
                          active={orderBy === 'campaignId'}
                          direction={orderBy === 'campaignId' ? order : 'asc'}
                          onClick={() => handleRequestSort('campaignId')}
                        >
                          Campaign ID
                        </StyledTableSortLabel>
                      </Box>
                      {showFilters && (
                        <TextField
                          size="small"
                          placeholder="Filter..."
                          value={filters.campaignId || ''}
                          onChange={(e) => handleFilter('campaignId', e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            mt: 1,
                            width: '100%',
                            '& .MuiInputBase-root': {
                              backgroundColor: 'background.paper',
                              fontSize: '0.75rem',
                              height: 32,
                            }
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 250 }}>
                      <Box>
                        <StyledTableSortLabel
                          active={orderBy === 'advertiser'}
                          direction={orderBy === 'advertiser' ? order : 'asc'}
                          onClick={() => handleRequestSort('advertiser')}
                        >
                          Advertiser
                        </StyledTableSortLabel>
                      </Box>
                      {showFilters && (
                        <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                          <Select
                            multiple
                            displayEmpty
                            value={selectedAdvertisers}
                            onChange={(e) => setSelectedAdvertisers(e.target.value as string[])}
                            renderValue={(selected) => selected.length > 0 ? `${selected.length} selected` : 'Select...'}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              backgroundColor: 'background.paper',
                              fontSize: '0.75rem',
                              height: 32,
                              '& .MuiSelect-select': {
                                py: 0,
                                display: 'flex',
                                alignItems: 'center',
                              }
                            }}
                          >
                            {uniqueAdvertisers.map(advertiser => (
                              <MenuItem key={advertiser} value={advertiser}>
                                <Checkbox checked={selectedAdvertisers.includes(advertiser)} />
                                {advertiser}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    {reportType === 'ad' && (
                      <>
                        <TableCell sx={{ minWidth: 140 }}>
                          <Box>
                            <StyledTableSortLabel
                              active={orderBy === 'adId'}
                              direction={orderBy === 'adId' ? order : 'asc'}
                              onClick={() => handleRequestSort('adId')}
                            >
                              Ad ID
                            </StyledTableSortLabel>
                          </Box>
                          {showFilters && (
                            <TextField
                              size="small"
                              placeholder="Filter..."
                              value={filters.adId || ''}
                              onChange={(e) => handleFilter('adId', e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                mt: 1,
                                width: '100%',
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'background.paper',
                                  fontSize: '0.75rem',
                                  height: 32,
                                }
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <Box>
                            <StyledTableSortLabel
                              active={orderBy === 'adsetId'}
                              direction={orderBy === 'adsetId' ? order : 'asc'}
                              onClick={() => handleRequestSort('adsetId')}
                            >
                              Adset ID
                            </StyledTableSortLabel>
                          </Box>
                          {showFilters && (
                            <TextField
                              size="small"
                              placeholder="Filter..."
                              value={filters.adsetId || ''}
                              onChange={(e) => handleFilter('adsetId', e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                mt: 1,
                                width: '100%',
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'background.paper',
                                  fontSize: '0.75rem',
                                  height: 32,
                                }
                              }}
                            />
                          )}
                        </TableCell>
                      </>
                    )}
                    <TableCell align="right" sx={{ minWidth: 100 }}>
                      <StyledTableSortLabel
                        active={orderBy === 'pnl'}
                        direction={orderBy === 'pnl' ? order : 'asc'}
                        onClick={() => handleRequestSort('pnl')}
                      >
                        P&L
                      </StyledTableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}>
                      <StyledTableSortLabel
                        active={orderBy === 'spend'}
                        direction={orderBy === 'spend' ? order : 'asc'}
                        onClick={() => handleRequestSort('spend')}
                      >
                        Spend
                      </StyledTableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}>
                      <StyledTableSortLabel
                        active={orderBy === 'revenue'}
                        direction={orderBy === 'revenue' ? order : 'asc'}
                        onClick={() => handleRequestSort('revenue')}
                      >
                        Revenue
                      </StyledTableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 90 }}>
                      <StyledTableSortLabel
                        active={orderBy === 'roas'}
                        direction={orderBy === 'roas' ? order : 'asc'}
                        onClick={() => handleRequestSort('roas')}
                      >
                        ROAS
                      </StyledTableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}>
                      <StyledTableSortLabel
                        active={orderBy === 'avgRpc'}
                        direction={orderBy === 'avgRpc' ? order : 'asc'}
                        onClick={() => handleRequestSort('avgRpc')}
                      >
                        AVG RPC
                      </StyledTableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 120 }}>
                      <StyledTableSortLabel
                        active={orderBy === 'avgCpp'}
                        direction={orderBy === 'avgCpp' ? order : 'asc'}
                        onClick={() => handleRequestSort('avgCpp')}
                      >
                        Cost/Purchase
                      </StyledTableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 110 }}>
                      <StyledTableSortLabel
                        active={orderBy === 'cplc'}
                        direction={orderBy === 'cplc' ? order : 'asc'}
                        onClick={() => handleRequestSort('cplc')}
                      >
                        Cost/Click
                      </StyledTableSortLabel>
                    </TableCell>
                    {!groupByDate && (
                      <>
                        <TableCell align="center" sx={{ minWidth: 150 }}>
                          <Box>
                            <StyledTableSortLabel
                              active={orderBy === 'roasTrend'}
                              direction={orderBy === 'roasTrend' ? order : 'asc'}
                              onClick={() => handleRequestSort('roasTrend')}
                            >
                              ROAS Trend
                            </StyledTableSortLabel>
                          </Box>
                          {showFilters && (
                            <TextField
                              size="small"
                              placeholder="e.g. >10"
                              value={filters.roasTrend || ''}
                              onChange={(e) => handleFilter('roasTrend', e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                mt: 1,
                                width: '100%',
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'background.paper',
                                  fontSize: '0.75rem',
                                  height: 32,
                                }
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ minWidth: 150 }}>
                          <Box>
                            <StyledTableSortLabel
                              active={orderBy === 'rpcTrend'}
                              direction={orderBy === 'rpcTrend' ? order : 'asc'}
                              onClick={() => handleRequestSort('rpcTrend')}
                            >
                              RPC Trend
                            </StyledTableSortLabel>
                          </Box>
                          {showFilters && (
                            <TextField
                              size="small"
                              placeholder="e.g. >10"
                              value={filters.rpcTrend || ''}
                              onChange={(e) => handleFilter('rpcTrend', e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                mt: 1,
                                width: '100%',
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'background.paper',
                                  fontSize: '0.75rem',
                                  height: 32,
                                }
                              }}
                            />
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </StyledTableHead>

                <StyledTableBody>
                  {paginatedData.map((row, index) => {
                    const isExpanded = !groupByDate && expandedCampaigns[row.campaignId];
                    const hasBreakdown = !groupByDate && row.dailyBreakdown && row.dailyBreakdown.length > 0;

                    return (
                      <>
                        {/* Main row */}
                        <TableRow key={`${row.campaignId}_${row.date}_${index}`}>
                          {groupByDate && <TableCell>{row.date}</TableCell>}
                          <TableCell
                            sx={{
                              maxWidth: 500,
                            }}
                            title={row.campaignName}
                          >
                            {hasBreakdown ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  '&:hover': {
                                    opacity: 0.8,
                                  },
                                }}
                                onClick={() => toggleCampaign(row.campaignId)}
                              >
                                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                                  {isExpanded ? <ChevronDownIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    color: 'primary.main',
                                  }}
                                >
                                  {row.campaignName}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {row.campaignName}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {row.campaignId}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(row.campaignId, 'Campaign ID')}
                                sx={{ p: 0.5 }}
                              >
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 250,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={row.advertiser || 'Unknown'}
                          >
                            {row.advertiser || 'Unknown'}
                          </TableCell>
                          {reportType === 'ad' && (
                            <>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {row.adId}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(row.adId || '', 'Ad ID')}
                                    sx={{ p: 0.5 }}
                                  >
                                    <CopyIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {row.adsetId}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(row.adsetId || '', 'Adset ID')}
                                    sx={{ p: 0.5 }}
                                  >
                                    <CopyIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </>
                          )}
                          <TableCell
                            align="right"
                            sx={{
                              color: row.pnl >= 0 ? theme.palette.success.main : theme.palette.error.main,
                              fontWeight: 600,
                            }}
                          >
                            {formatCurrency(row.pnl)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: theme.palette.error.main }}>
                            {formatCurrency(row.spend)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: theme.palette.success.main }}>
                            {formatCurrency(row.revenue)}
                          </TableCell>
                          <TableCell align="right">
                            {row.roas ? `${formatNumber(row.roas, 2)}x` : ''}
                          </TableCell>
                          <TableCell align="right">
                            {row.avgRpc !== null ? formatCurrency(row.avgRpc) : ''}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.avgCpp, true)}
                          </TableCell>
                          <TableCell align="right">
                            {row.cplc !== null ? formatCurrency(row.cplc) : ''}
                          </TableCell>
                          {!groupByDate && (
                            <>
                              <TableCell align="center">
                                <TrendChart
                                  data={(row.dailyBreakdown || []).map(d => ({
                                    date: d.date,
                                    value: d.roas,
                                  }))}
                                  colorThresholds={[
                                    { value: 1.25, color: '#10b981' },
                                    { value: 1.05, color: '#22c55e' },
                                    { value: 0.8, color: '#f59e0b' },
                                    { value: 0, color: '#ef4444' },
                                  ]}
                                  valueFormatter={(v) => `${(v * 100).toFixed(1)}%`}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TrendChart
                                  data={(row.dailyBreakdown || []).map(d => ({
                                    date: d.date,
                                    value: d.avgRpc,
                                  }))}
                                  useNormalization
                                  colorThresholds={[
                                    { value: 0.6, color: '#10b981' },
                                    { value: 0.4, color: '#22c55e' },
                                    { value: 0.25, color: '#f59e0b' },
                                    { value: 0, color: '#ef4444' },
                                  ]}
                                  valueFormatter={(v) => `$${v.toFixed(2)}`}
                                />
                              </TableCell>
                            </>
                          )}
                        </TableRow>

                        {/* Daily breakdown rows (when expanded) */}
                        {isExpanded && row.dailyBreakdown && row.dailyBreakdown.map((day, dayIndex) => (
                          <TableRow
                            key={`${index}-${dayIndex}`}
                            sx={{ backgroundColor: 'action.hover' }}
                          >
                            <TableCell sx={{ pl: 8 }}>
                              {day.date}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {row.campaignId}
                            </TableCell>
                            <TableCell
                            sx={{
                              maxWidth: 250,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={row.advertiser || 'Unknown'}
                          >
                            {row.advertiser || 'Unknown'}
                          </TableCell>
                            {reportType === 'ad' && (
                              <>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                              </>
                            )}
                            <TableCell
                              align="right"
                              sx={{
                                color: day.pnl >= 0 ? theme.palette.success.main : theme.palette.error.main,
                                fontWeight: 500,
                              }}
                            >
                              {formatCurrency(day.pnl)}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(day.spend)}</TableCell>
                            <TableCell align="right">{formatCurrency(day.revenue)}</TableCell>
                            <TableCell align="right">
                              {day.roas ? `${formatNumber(day.roas, 2)}x` : ''}
                            </TableCell>
                            <TableCell align="right">
                              {day.avgRpc !== null ? formatCurrency(day.avgRpc) : ''}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(day.avgCpp, true)}
                            </TableCell>
                            <TableCell></TableCell>
                            {!groupByDate && (
                              <>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                      </>
                    );
                  })}
                </StyledTableBody>
              </Table>
            </StyledTableContainer>

            <StyledTablePagination
              rowsPerPageOptions={[10, 25, 50, 100, 500]}
              count={sortedData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* No Data */}
      {!loading && rawData.length === 0 && (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
              No data available. Click "Generate Report" to load data.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Help Modal */}
      <HelpModal open={showHelpModal} onClose={() => setShowHelpModal(false)} />
    </Box>
  );
};

export default InsightsPage;
